import { FC, useEffect, useMemo, useState } from 'react';
// import chart from '../api/chart2.json';
import { useSelector } from 'react-redux';
import { getMonthlySalesInfo, getMonthlySalesInfoLoader } from 'redux/reducers/dashboard/selectors';
import { TMonthlySalesInfoResponse } from 'redux/reducers/dashboard/types';
import { Loader } from 'ui/Loader';
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import { nanoid } from '@reduxjs/toolkit';
import { Tooltip } from './Tooltip';
import { COLOR_ARRAY, SCALE_COEFFICIENT_MAX, SCALE_COEFFICIENT_MIN } from '../constants';
import './styles.css';
import { ChartFooter } from './ChartFooter';
import { ChartBody } from './ChartBody';

const ROWS = Array.from(new Array(12));

export const Chart: FC = () => {
	const [documentWidth, setDocumentWidth] = useState(window.innerWidth);
	const [namePartOfLine, setNamePartOfLine] = useState('');
	const [showTooltip, setShowTooltip] = useState(false);
	const [topPositionTooltip, setTopPositionTooltip] = useState(0);
	const [leftPositionTooltip, setLeftPositionTooltip] = useState(0);
	const [showDetailedData, setShowDetailedData] = useState(false);
	const [amountTooltip, setAmountTooltip] = useState(0);

	const chart = useSelector(getMonthlySalesInfo);
	const chartIsLoading = useSelector(getMonthlySalesInfoLoader);

	// if (!chart) return <div>Loading...</div>;

	const allChartArrayFoo = (chrt: TMonthlySalesInfoResponse) => {
		if (!chrt || chrt.length === 0) return [];
		const allChartTmp: string[] = [];
		for (let i = 0; i < chrt.length; i++) {
			for (let j = 0; j < chrt[i].x.length; j++) {
				if (!allChartTmp.includes(chrt[i].x[j].name)) allChartTmp.push(chrt[i].x[j].name);
			}
		}
		return allChartTmp;
	};

	const allChartColorFoo = (chrt: string[]) => {
		const colors: any = {};
		for (let i = 0; i < chrt.length; i++) {
			colors[chrt[i]] = COLOR_ARRAY[i];
		}
		return colors;
	};

	const allChartArray = allChartArrayFoo(chart as TMonthlySalesInfoResponse);
	const chartColors = useMemo(() => allChartColorFoo(allChartArray), [allChartArray]);

	useEffect(() => {
		function handleResize() {
			setDocumentWidth(window.innerWidth);
		}
		window.addEventListener('resize', handleResize);
	});
	const scaleCoefficient =
		documentWidth < 1000 ? 0 : documentWidth < 1400 ? SCALE_COEFFICIENT_MIN : SCALE_COEFFICIENT_MAX;
	const arrayX = chart?.map((item) => item.x.reduce((acc, num) => +acc + Number(num.amount), 0));

	const maxX = Math.max.apply(null, arrayX as number[]);
	const maxD = Math.ceil(maxX + scaleCoefficient * maxX);
	const razr = Math.ceil(maxD).toString().length - 1;
	const scaleX = Math.ceil(maxD / 10 ** razr) * 10 ** razr;
	const hRazr = +Math.ceil(scaleX).toString()[0];
	const mRazr = scaleX / hRazr;
	const sArray: number[] = [];
	if (documentWidth < 1000 || hRazr > 3)
		for (let i = 0; i < hRazr; i += 1) {
			sArray.push(i * mRazr);
		}
	else
		for (let i = 0; i < hRazr * 4; i += 1) {
			sArray.push((i * mRazr) / 4);
		}

	const setShowDetailedDataHandler = (checked: boolean) => {
		setShowDetailedData(checked);
	};

	const hoverHandler = (index: number, num: number, amount: number, name: string) => {
		if (!amount) return;
		setShowTooltip(true);
		setTopPositionTooltip(40 * index + 20);
		setNamePartOfLine(name);
		let shiftX = 0;
		if (showDetailedData && chart) {
			const nameNew =
				chart[index].x[num].name === 'B2C Internal EU Sales'
					? 'B2C Domestic EU/UK Sales'
					: chart[index].x[num].name;
			setAmountTooltip(amount);
			for (let i = 0; i <= num; i += 1) {
				if (i > 0) {
					shiftX += (chart[index].x[i - 1].amount * 100) / scaleX;
				}
			}
			shiftX += (chart[index].x[num].amount * 10) / scaleX;
		} else {
			setAmountTooltip(amount);
			shiftX += (amount * 10) / scaleX;
		}
		setLeftPositionTooltip(shiftX);
	};

	const unHoverHandler = () => {
		setShowTooltip(false);
	};
	return (
		<Card
			sx={{
				mb: 2,
				borderRadius: '12px',
				'& .MuiCardContent-root': { padding: '8px 16px 16px 16px!important' },
			}}
		>
			<CardContent
				sx={{
					fontWeight: 400,
					fontSize: '1rem',
					lineHeight: 1.5,
					letterSpacing: '0.00938em',
					position: 'relative',
				}}
			>
				<Typography
					variant="h5"
					color="primary"
					sx={{
						fontSize: '1.2rem',
						fontWeight: 'bold',
						marginBottom: '30px',
					}}
				>
					Total Sales Volume
				</Typography>
				{chartIsLoading && (
					<Box
						sx={{
							pb: '96px',
							pt: '36px',
						}}
					>
						{ROWS.map(() => (
							<Skeleton
								key={nanoid()}
								variant="rectangular"
								height="20px"
								sx={{
									mb: '20px',
								}}
							/>
						))}
					</Box>
				)}

				{chart?.length && !chartIsLoading && (
					<div
						style={{
							overflowX: 'auto',
							position: 'relative',
							paddingTop: '36px',
						}}
					>
						<ChartBody
							chart={chart}
							showDetailedData={showDetailedData}
							hoverHandler={hoverHandler}
							unHoverHandler={unHoverHandler}
							scaleX={scaleX}
							sArray={sArray}
							colors={chartColors}
						/>
						<ChartFooter
							showDetailedData={showDetailedData}
							setShowDetailedDataHandler={setShowDetailedDataHandler}
							charts={allChartArray}
							colors={chartColors}
						/>
						<Tooltip
							show={showTooltip}
							topPosition={topPositionTooltip}
							color={
								showDetailedData ? chartColors[namePartOfLine] : COLOR_ARRAY[COLOR_ARRAY.length - 1]
							}
							amount={amountTooltip}
							leftPosition={leftPositionTooltip}
						/>
					</div>
				)}
			</CardContent>
		</Card>
	);
};
